import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import JSSolutionLogo from "@/components/JSSolutionLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Preencha email e senha");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password,
      });
      const data = res.data;
      if (data?.access_token) {
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        toast.success("Login realizado com sucesso!");
        const user = data.user;
        if (!user.company_id && !user.tenant_id) {
          navigate(user.status === "pending_approval" ? "/onboarding/pending-approval" : "/onboarding/join-company");
        } else if ((user.role_name || "").toLowerCase().match(/^gar[cç]om$|^waiter$/)) {
          navigate("/admin/comanda");
        } else {
          navigate("/admin/welcome");
        }
      } else {
        toast.error("Credenciais inválidas");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md p-8">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6"
          data-testid="back-button"
          type="button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Voltar</span>
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4">
            <JSSolutionLogo size="2xl" animated={true} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Painel Administrativo</h1>
          <p className="text-gray-600">Entre com suas credenciais</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="email"
                name="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 pr-10"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            data-testid="login-submit"
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <button
            onClick={() => navigate("/forgot-password")}
            className="block w-full text-blue-600 hover:text-blue-700 font-medium text-sm"
            data-testid="forgot-password-link"
          >
            Esqueceu sua senha?
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>

          <button
            onClick={() => navigate("/register-new")}
            className="block w-full text-emerald-600 hover:text-emerald-700 font-medium"
            data-testid="register-link"
          >
            Criar nova conta
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-sm">
          <a href="/contato" className="text-gray-500 hover:text-emerald-600 transition-colors">Contato</a>
          <span className="text-gray-300">|</span>
          <a href="https://wa.me/5517996462254" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 transition-colors">
            Suporte WhatsApp
          </a>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} JsSolution. Todos os direitos reservados.
        </p>
      </Card>
    </div>
  );
};

export default AdminLogin;
